import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SITE_NAME } from "@/mocks/site";
import { COLUMNS } from "@/mocks/site";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/?q=${encodeURIComponent(q)}` : "/");
    if (q) window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navMain = [
    { to: "/", label: "首页", icon: "ri-home-line" },
    { to: "/about", label: "关于我们", icon: "ri-file-user-line" },
  ];

  return (
    <header className={`sticky top-0 z-50 w-full border-b border-background-200/70 backdrop-blur transition-colors ${scrolled ? "bg-background-50/95" : "bg-background-50/90"}`}>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap" aria-label={SITE_NAME}>
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 text-lg text-background-50 shadow-sm">
            <i className="ri-chat-smile-3-line" aria-hidden="true" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-xl font-black text-foreground-950">{SITE_NAME}</span>
            <span className="mt-0.5 text-[11px] text-foreground-600">校园瓜料 · 天天直击</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navMain.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-foreground-700 whitespace-nowrap transition-colors hover:bg-background-200/70 hover:text-primary-700"
            >
              <i className={`${item.icon} text-base`} aria-hidden="true" />
              {item.label}
            </Link>
          ))}
          <div className="mx-1 h-5 w-px bg-background-300" aria-hidden="true" />
          {COLUMNS.map((c) => (
            <Link
              key={c.slug}
              to={`/column/${c.slug}`}
              className="rounded-full px-3.5 py-2 text-sm text-foreground-700 whitespace-nowrap transition-colors hover:bg-primary-100 hover:text-primary-800"
            >
              {c.name}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} role="search" className="hidden items-center md:flex">
          <div className="relative">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-foreground-500" aria-hidden="true" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="search"
              aria-label="搜索瓜文"
              placeholder="搜点新鲜的…"
              className="h-9 w-44 rounded-full border border-background-300 bg-background-50 pl-9 pr-3 text-sm outline-none transition-all placeholder:text-foreground-400 focus:w-52 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 lg:w-52"
            />
          </div>
        </form>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-700 hover:bg-background-200/70 lg:hidden"
          aria-label={open ? "收起菜单" : "展开菜单"}
          aria-expanded={open}
        >
          <i className={open ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"} aria-hidden="true" />
        </button>
      </div>

      {open && (
        <div className="border-t border-background-200/70 bg-background-50 px-4 py-3 lg:hidden">
          <div className="flex flex-col gap-1">
            <Link to="/" className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm hover:bg-background-200/70">
              <i className="ri-home-line" aria-hidden="true" /> 首页
            </Link>
            {COLUMNS.map((c) => (
              <Link key={c.slug} to={`/column/${c.slug}`} className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm hover:bg-background-200/70">
                <i className="ri-arrow-right-s-line text-primary-500" aria-hidden="true" /> {c.name}
              </Link>
            ))}
            <Link to="/about" className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm hover:bg-background-200/70">
              <i className="ri-file-user-line" aria-hidden="true" /> 关于我们
            </Link>
            <form onSubmit={submitSearch} role="search" className="mt-1 flex items-center gap-2 px-1">
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500" aria-hidden="true" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="search"
                  aria-label="搜索瓜文"
                  placeholder="搜点新鲜的…"
                  className="h-10 w-full rounded-lg border border-background-300 bg-background-50 pl-9 pr-3 text-sm outline-none focus:border-primary-400"
                />
              </div>
              <button type="submit" className="h-10 whitespace-nowrap rounded-lg bg-primary-500 px-4 text-sm text-background-50 hover:bg-primary-600">
                搜索
              </button>
            </form>
          </div>
        </div>
      )}
    </header>
  );
}